import { PulseCompaniesResponse } from 'api/pulse/pulseCompanies'
import { ApiRequest, DemoApiMock, DemoData } from '../types'
import { getDemoData, matchRestRequest } from '../utils'
import { PulsePositionTypes } from 'api/pulse/pulsePositionTypes'
import { FillRateMetrics, PulseFilters, TimePeriod } from 'api/pulse'
import { FillRateTimeseries } from 'api/pulse/fillRateTimeseries'
import { DemoPulseFillData } from '../data/pulse'
import { groupBy } from 'lodash'
import {
  parseISO,
  format,
  startOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  isWithinInterval,
} from 'date-fns'
import { apiDateFormat } from 'api'
import { pulseFiltersSchema } from 'api/pulse/types'
import { z } from 'zod'
import { FillRateByCompany } from 'api/pulse/fillRateByCompany'
import { ShiftNoticeMetrics } from 'api/pulse/shiftNoticeMetrics'
import { ShiftNoticeTimeseries } from 'api/pulse/shiftNoticeTimeseries'
import { FillRateByNoticeTimeseries } from 'api/pulse/fillRateByNoticeTimeseries'
import { FillPayNoticeByLocation } from 'api/pulse/fillPayNoticeByLocation'
import { PayRateByPosition } from 'api/pulse/payRateByPosition'
import { SpendMetrics } from 'api/pulse/spendMetrics'
import { SpendTimeseries } from 'api/pulse/spendTimeseries'
import { SpendByLocation } from 'api/pulse/spendByLocation'
import { SpendPerWorkerTimeseries } from 'api/pulse/spendPerWorkerTimeseries'
import { PayRateMetrics } from 'api/pulse/payRateMetrics'
import { PayRateTimeseries } from 'api/pulse/payRateTimeseries'
import { PayRateByLocation } from 'api/pulse/payRateByLocation'
import { PayRateMarketByLocation } from 'api/pulse/payRateMarketByLocation'
import { RatingMetrics } from 'api/pulse/ratingMetrics'
import { TopCompliments } from 'api/pulse/topCompliments'
import { TopComplaints } from 'api/pulse/topComplaints'
import { RatingsTimeseries } from 'api/pulse/ratingsTimeseries'
import { RatingsByLocation } from 'api/pulse/ratingsByLocation'
import { TopWorkers } from 'api/pulse/topWorkers'

type PulseFiltersSchema = z.infer<typeof pulseFiltersSchema>

function getFilteredPulseFillData(
  demoData: DemoData,
  request: ApiRequest
): DemoPulseFillData[] {
  const positionTypes = (request.data as PulseFiltersSchema).positionTypes ?? []
  const dateFrom = parseISO((request.data as PulseFiltersSchema).dateFrom)
  const dateTo = parseISO((request.data as PulseFiltersSchema).dateTo)

  return (
    demoData.pulseFillData?.filter(
      (data) =>
        (!positionTypes.length || positionTypes.includes(data.positionType)) &&
        isWithinInterval(parseISO(data.date), {
          start: dateFrom,
          end: dateTo,
        })
    ) ?? []
  )
}

function normalizeDateByTimePeriod(
  date: string,
  timePeriod: TimePeriod
): string {
  const parsedDate = parseISO(date)
  switch (timePeriod) {
    case TimePeriod.Daily:
      return date
    case TimePeriod.Weekly:
      return format(startOfWeek(parsedDate, { weekStartsOn: 0 }), apiDateFormat)
    case TimePeriod.Monthly:
      return format(startOfMonth(parsedDate), apiDateFormat)
    case TimePeriod.Quarterly:
      return format(startOfQuarter(parsedDate), apiDateFormat)
    case TimePeriod.Yearly:
      return format(startOfYear(parsedDate), apiDateFormat)
    default:
      return date
  }
}

// Companies selector
const mockGetPulseCompanies: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(request, '/company/pulse/companies', 'GET'),
  getResponse: async () => {
    return {
      companies: [
        {
          id: 151,
          name: 'DemoCo',
        },
      ],
    } as PulseCompaniesResponse
  },
}

// Position types selector
const mockGetPulsePositionTypes: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(request, '/company/pulse/position_types', 'GET'),
  getResponse: async () => {
    const demoData = getDemoData()

    const positionTypes = new Set(
      (demoData.pulseFillData ?? []).map((data) => data.positionType)
    )

    return {
      position_types: Array.from(positionTypes).sort(),
    } as PulsePositionTypes
  },
}

// Operations - fill metrics
const mockGetPulseFillMetrics: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/operations/fill_rate_metrics',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const fillRate =
      filteredData.reduce((acc, data) => acc + data.fillRate, 0) /
      filteredData.length
    const totalRequestedShifts = filteredData.length
    const totalFilledShifts = filteredData.filter(
      (data) => data.fillRate === 1
    ).length
    const totalHoursWorked = filteredData.reduce(
      (acc, data) => acc + data.numHoursWorked,
      0
    )

    return {
      avg_fill_rate: fillRate,
      total_requested_shifts: totalRequestedShifts,
      total_hours_worked: totalHoursWorked,
      total_filled_shifts: totalFilledShifts,
    } as FillRateMetrics
  },
}

// Fill rate timeseries
const mockGetPulseFillRateTimeseries: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/operations/fill_rate_timeseries',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const timePeriod =
      (request.data as PulseFilters).timePeriod ?? TimePeriod.Monthly

    // group filteredData by timePeriod
    const groupedData = groupBy(filteredData, (data) =>
      normalizeDateByTimePeriod(data.date, timePeriod)
    )

    return Object.entries(groupedData).map(
      ([date, data]) =>
        ({
          shift_date: date,
          workers_needed_final: data.reduce(
            (acc, data) => acc + data.workersNeeded,
            0
          ),
          fill_rate:
            data.reduce((acc, data) => acc + data.fillRate, 0) / data.length,
        } as FillRateTimeseries[0])
    )
  },
}

// Fill rate by company
const mockGetPulseFillRateByCompany: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/operations/fill_rate_by_location',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const timePeriod =
      (request.data as PulseFilters).timePeriod ?? TimePeriod.Monthly

    // group filteredData by timePeriod and locationName
    const groupedData = groupBy(
      filteredData,
      (data) =>
        normalizeDateByTimePeriod(data.date, timePeriod) +
        '||' +
        data.locationName
    )

    return Object.entries(groupedData).map(([key, data]) => {
      const [date, locationName] = key.split('||')
      return {
        company_name: locationName,
        shift_date: date,
        workers_needed_final: data.reduce(
          (acc, data) => acc + data.workersNeeded,
          0
        ),
        fill_rate:
          data.reduce((acc, data) => acc + data.fillRate, 0) / data.length,
      } as FillRateByCompany[0]
    })
  },
}

// Shift notice metrics
const mockGetPulseShiftNoticeMetrics: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/operations/shift_notice_metrics',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const averageHoursNotice =
      filteredData.reduce((acc, data) => acc + data.numHoursNotice, 0) /
      filteredData.length

    const shortNoticeRate =
      filteredData.filter((data) => data.numHoursNotice < 12).length /
      filteredData.length

    return {
      average_hours_notice: averageHoursNotice,
      short_notice_rate: shortNoticeRate,
    } as ShiftNoticeMetrics
  },
}

// Shift notice timeseries
const mockGetPulseShiftNoticeTimeseries: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/operations/shift_notice_timeseries',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const timePeriod =
      (request.data as PulseFilters).timePeriod ?? TimePeriod.Monthly

    // group filteredData by timePeriod and noticeCategory
    const groupedData = groupBy(
      filteredData,
      (data) =>
        normalizeDateByTimePeriod(data.date, timePeriod) +
        '||' +
        data.noticeCategory
    )

    return Object.entries(groupedData).map(([key, data]) => {
      const [date, noticeCategory] = key.split('||')
      return {
        shift_date: date,
        notice_category: noticeCategory,
        workers_needed_final: data.reduce(
          (acc, data) => acc + data.workersNeeded,
          0
        ),
      } as ShiftNoticeTimeseries[0]
    })
  },
}

// Shift rate by notice timeseries
const mockGetPulseShiftRateByNoticeTimeseries: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/operations/fill_rate_by_notice_timeseries',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const timePeriod =
      (request.data as PulseFilters).timePeriod ?? TimePeriod.Monthly

    // group filteredData by timePeriod and noticeCategory
    const groupedData = groupBy(
      filteredData,
      (data) =>
        normalizeDateByTimePeriod(data.date, timePeriod) +
        '||' +
        (data.numHoursNotice < 12 ? 'Short Notice' : 'Long Notice')
    )

    return Object.entries(groupedData).map(([key, data]) => {
      const [date, noticeCategory] = key.split('||')
      return {
        shift_date: date,
        notice_category: noticeCategory,
        fill_rate:
          data.reduce((acc, data) => acc + data.fillRate, 0) / data.length,
        shifts_requested: data.length,
      } as FillRateByNoticeTimeseries[0]
    })
  },
}

// Fill pay notice by location
const mockGetPulseFillPayNoticeByLocation: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/operations/fill_pay_notice_breakdown',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const timePeriod =
      (request.data as PulseFilters).timePeriod ?? TimePeriod.Monthly

    // group filteredData by timePeriod and locationName
    const groupedData = groupBy(
      filteredData,
      (data) =>
        normalizeDateByTimePeriod(data.date, timePeriod) +
        '||' +
        data.locationName
    )

    return Object.entries(groupedData).map(([key, data]) => {
      const [date, locationName] = key.split('||')

      const rowsShortNotice = data.filter((data) => data.numHoursNotice < 12)
      const rowsLongNotice = data.filter((data) => data.numHoursNotice >= 12)

      const avgFillRateLongNotice =
        rowsLongNotice.length > 0
          ? rowsLongNotice.reduce((acc, data) => acc + data.fillRate, 0) /
            rowsLongNotice.length
          : null
      const avgFillRateShortNotice =
        rowsShortNotice.length > 0
          ? rowsShortNotice.reduce((acc, data) => acc + data.fillRate, 0) /
            rowsShortNotice.length
          : null
      const avgPayLongNotice =
        rowsLongNotice.length > 0
          ? rowsLongNotice.reduce((acc, data) => acc + data.payRate, 0) /
            rowsLongNotice.length
          : null
      const avgPayShortNotice =
        rowsShortNotice.length > 0
          ? rowsShortNotice.reduce((acc, data) => acc + data.payRate, 0) /
            rowsShortNotice.length
          : null
      const avgPayRateWithCompanyBonus =
        rowsLongNotice.length > 0
          ? rowsLongNotice.reduce((acc, data) => acc + data.payRate, 0) /
            rowsLongNotice.length
          : null
      const changeInFillLongVsShortNotice =
        rowsLongNotice.length > 0 && rowsShortNotice.length > 0
          ? rowsLongNotice.reduce((acc, data) => acc + data.fillRate, 0) /
              rowsLongNotice.length -
            rowsShortNotice.reduce((acc, data) => acc + data.fillRate, 0) /
              rowsShortNotice.length
          : null
      const changeInPayLongVsShortNotice =
        avgPayLongNotice && avgPayShortNotice
          ? (avgPayLongNotice - avgPayShortNotice) / avgPayLongNotice
          : null
      const lateNoticeShifts =
        rowsShortNotice.length /
        (rowsShortNotice.length + rowsLongNotice.length)

      return {
        company_name: locationName,
        location_market: locationName,
        shift_date: date,
        workers_needed_final: data.reduce(
          (acc, data) => acc + data.workersNeeded,
          0
        ),
        fill_rate:
          data.reduce((acc, data) => acc + data.fillRate, 0) / data.length,
        fill_rate_long_notice: avgFillRateLongNotice,
        fill_rate_short_notice: avgFillRateShortNotice,
        change_in_fill_long_vs_short_notice: changeInFillLongVsShortNotice,
        change_in_pay_long_vs_short_notice: changeInPayLongVsShortNotice,
        late_notice_shifts: lateNoticeShifts,
        avg_pay_rate_with_company_bonus: avgPayRateWithCompanyBonus,
        avg_pay_short_notice: avgPayShortNotice,
        avg_pay_long_notice: avgPayLongNotice,
      } as FillPayNoticeByLocation[0]
    })
  },
}

// Pay rate by position
const mockGetPulsePayRateByPosition: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/operations/pay_rate_by_position',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    // group filteredData by timePeriod and positionType
    const groupedData = groupBy(
      filteredData,
      (data) => data.positionType + '||' + data.payRateBinning
    )

    return Object.entries(groupedData)
      .map(([key, data]) => {
        const [positionType, payRateBinning] = key.split('||')

        return {
          position_type: positionType,
          fill_rate:
            data.reduce((acc, data) => acc + data.fillRate, 0) / data.length,
          pay_rate: payRateBinning,
          filled_shifts: data.length,
          location_count: data.reduce((acc, data) => acc + data.locationId, 0),
          repeat_worker_rate:
            data.reduce((acc, data) => acc + data.repeatWorkerRate, 0) /
            data.length,
          reliability_rate:
            data.reduce((acc, data) => acc + data.reliabilityRate, 0) /
            data.length,
          avg_worker_to_manager_rating:
            data.reduce((acc, data) => acc + data.avgWorkerToManagerRating, 0) /
            data.length,
          avg_market_pay_rate:
            data.reduce((acc, data) => acc + data.avgMarketPayRate, 0) /
            data.length,
        } as PayRateByPosition[0]
      })
      .sort((a, b) => {
        const positionTypeCompare = a.position_type.localeCompare(
          b.position_type
        )
        if (positionTypeCompare !== 0) return positionTypeCompare
        return a.pay_rate.localeCompare(b.pay_rate)
      })
  },
}

// Spend metrics
const mockGetPulseSpendMetrics: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(request, '/company/pulse/cost/spend_metrics', 'POST'),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const baseWagesSpend = filteredData.reduce((acc, data) => {
      const spend = data.payRate * data.numHoursWorked
      return acc + spend
    }, 0)
    const bonusSpend = filteredData.reduce((acc, data) => {
      const spend = data.payRate * data.numHoursWorked * data.bonusRate
      return acc + spend
    }, 0)
    const otherSpendOrDiscount = filteredData.reduce((acc, data) => {
      const spend = data.payRate * data.numHoursWorked * data.creditRate
      return acc + spend * 0.05
    }, 0)
    return {
      total_spend: baseWagesSpend + bonusSpend + otherSpendOrDiscount,
      wages_spend: baseWagesSpend,
      bonus_spend: bonusSpend,
      other_spend_or_discount: otherSpendOrDiscount,
    } as SpendMetrics
  },
}

// Spend timeseries
const mockGetPulseSpendTimeseries: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(request, '/company/pulse/cost/spend_timeseries', 'POST'),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const timePeriod =
      (request.data as PulseFilters).timePeriod ?? TimePeriod.Monthly

    const groupedData = groupBy(filteredData, (data) =>
      normalizeDateByTimePeriod(data.date, timePeriod)
    )

    return Object.entries(groupedData).flatMap(([date, data]) => {
      return [
        {
          revenue_date_pst: date,
          spend_category: 'Base Wages',
          adj_gross_revenue: data.reduce(
            (acc, data) => acc + data.payRate * data.numHoursWorked,
            0
          ),
        },
        {
          revenue_date_pst: date,
          spend_category: 'Bonus',
          adj_gross_revenue: data.reduce(
            (acc, data) =>
              acc + data.payRate * data.numHoursWorked * data.bonusRate,
            0
          ),
        },
        {
          revenue_date_pst: date,
          spend_category: 'Other Discount',
          adj_gross_revenue: data.reduce(
            (acc, data) =>
              acc + data.payRate * data.numHoursWorked * data.creditRate,
            0
          ),
        },
      ] as SpendTimeseries
    })
  },
}

// Spend by location
const mockGetPulseSpendByLocation: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(request, '/company/pulse/cost/spend_by_location', 'POST'),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const timePeriod =
      (request.data as PulseFilters).timePeriod ?? TimePeriod.Monthly

    const groupedData = groupBy(filteredData, (data) =>
      normalizeDateByTimePeriod(data.date, timePeriod)
    )

    return Object.entries(groupedData).flatMap(([date, data]) => {
      return [
        {
          company_name: data[0].locationName,
          revenue_date: date,
          spend_category: 'Base Wages',
          total_spend: data.reduce(
            (acc, data) => acc + data.payRate * data.numHoursWorked,
            0
          ),
        },
        {
          company_name: data[0].locationName,
          revenue_date: date,
          spend_category: 'Bonus',
          total_spend: data.reduce(
            (acc, data) =>
              acc + data.payRate * data.numHoursWorked * data.bonusRate,
            0
          ),
        },
        {
          company_name: data[0].locationName,
          revenue_date: date,
          spend_category: 'Other Discount',
          total_spend: data.reduce(
            (acc, data) =>
              acc + data.payRate * data.numHoursWorked * data.creditRate,
            0
          ),
        },
      ] as SpendByLocation
    })
  },
}

// Spend per worker timeseries
const mockGetPulseSpendPerWorkerTimeseries: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/cost/spend_per_worker_timeseries',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const timePeriod =
      (request.data as PulseFilters).timePeriod ?? TimePeriod.Monthly

    const groupedData = groupBy(filteredData, (data) =>
      normalizeDateByTimePeriod(data.date, timePeriod)
    )

    return Object.entries(groupedData).flatMap(([date, data]) => {
      return {
        shift_date: date,
        avg_spend_per_worker:
          data.reduce(
            (acc, data) =>
              acc + (data.payRate * data.numHoursWorked) / data.workersFilled,
            0
          ) / data.length,
      } as SpendPerWorkerTimeseries[0]
    })
  },
}

// Pay rate metrics
const mockGetPulsePayRateMetrics: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(request, '/company/pulse/cost/pay_rate_metrics', 'POST'),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    return {
      avg_pay_rate:
        filteredData.reduce((acc, data) => acc + data.payRate, 0) /
        filteredData.length,
      avg_bonus_rate:
        filteredData.reduce(
          (acc, data) => acc + data.payRate * data.bonusRate,
          0
        ) / filteredData.length,
      avg_pay_rate_with_company_bonus:
        filteredData.reduce(
          (acc, data) => acc + data.payRate + data.payRate * data.bonusRate,
          0
        ) / filteredData.length,
    } as PayRateMetrics
  },
}

// Pay rate timeseries
const mockGetPulsePayRateTimeseries: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/operations/pay_rate_timeseries',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const timePeriod =
      (request.data as PulseFilters).timePeriod ?? TimePeriod.Monthly

    const groupedData = groupBy(
      filteredData,
      (data) =>
        normalizeDateByTimePeriod(data.date, timePeriod) +
        '||' +
        data.positionType
    )

    return Object.entries(groupedData).map(([key, data]) => {
      const [date, positionType] = key.split('||')

      return {
        shift_date: date,
        position_type: positionType,
        avg_pay_rate:
          data.reduce((acc, data) => acc + data.payRate, 0) / data.length,
      } as PayRateTimeseries[0]
    })
  },
}

// Pay rate by location
const mockGetPulsePayRateByLocation: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/cost/pay_rate_by_location',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const timePeriod =
      (request.data as PulseFilters).timePeriod ?? TimePeriod.Monthly

    const locationGroupedData = groupBy(
      filteredData,
      (data) => data.locationName + '||' + data.positionType
    )

    return Object.entries(locationGroupedData).flatMap(([key, data]) => {
      const [locationName, positionType] = key.split('||')

      const dateGroupedData = groupBy(data, (data) =>
        normalizeDateByTimePeriod(data.date, timePeriod)
      )

      const sortedDates = Object.keys(dateGroupedData).sort((a, b) =>
        a.localeCompare(b)
      )

      return sortedDates.map((date, index) => {
        const currentPeriodData = dateGroupedData[date]

        const previousPeriodData =
          index > 0 ? dateGroupedData[sortedDates[index - 1]] : null

        const avgPayRateCurrentPeriod =
          currentPeriodData.reduce((acc, data) => acc + data.payRate, 0) /
          currentPeriodData.length
        const avgPayRatePreviousPeriod = previousPeriodData
          ? previousPeriodData.reduce((acc, data) => acc + data.payRate, 0) /
            previousPeriodData.length
          : null

        const changeInPayRateCurrentPeriod = avgPayRatePreviousPeriod
          ? (avgPayRateCurrentPeriod - avgPayRatePreviousPeriod) /
            avgPayRatePreviousPeriod
          : null

        const fillRateCurrentPeriod =
          currentPeriodData.reduce((acc, data) => acc + data.fillRate, 0) /
          currentPeriodData.length
        const fillRatePreviousPeriod = previousPeriodData
          ? previousPeriodData.reduce((acc, data) => acc + data.fillRate, 0) /
            previousPeriodData.length
          : null

        const periodChangeInFillRateCurrentPeriod = fillRatePreviousPeriod
          ? (fillRateCurrentPeriod - fillRatePreviousPeriod) /
            fillRatePreviousPeriod
          : null

        return {
          company_name: locationName,
          position_type: positionType,
          shift_date: date,
          avg_pay_rate_current_period: avgPayRateCurrentPeriod,
          avg_pay_rate_previous_period: avgPayRatePreviousPeriod,
          change_in_pay_rate_current_period: changeInPayRateCurrentPeriod,
          period_change_in_fill_rate_current_period:
            periodChangeInFillRateCurrentPeriod,
          fill_rate_current_period: fillRateCurrentPeriod,
          fill_rate_previous_period: fillRatePreviousPeriod,
        } as PayRateByLocation[0]
      })
    })
  },
}

// Pay rate market by location
const mockGetPulsePayRateMarketByLocation: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/cost/pay_rate_market_by_location',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const locationGroupedData = groupBy(
      filteredData,
      (data) => data.locationName + '||' + data.positionType
    )

    return Object.entries(locationGroupedData)
      .map(([key, data]) => {
        const [locationName, positionType] = key.split('||')

        return {
          company_name: locationName,
          position_type: positionType,
          avg_pay_rate_with_company_bonus:
            data.reduce((acc, data) => acc + data.payRate, 0) / data.length,
          avg_market_pay_rate:
            data.reduce((acc, data) => acc + data.avgMarketPayRate, 0) /
            data.length,
          fill_rate:
            data.reduce((acc, data) => acc + data.fillRate, 0) / data.length,
          pay_rate_difference_with_market:
            data.reduce(
              (acc, data) => acc + data.payRate - data.avgMarketPayRate,
              0
            ) / data.length,
        } as PayRateMarketByLocation[0]
      })
      .sort((a, b) => {
        const locationNameCompare = a.company_name.localeCompare(b.company_name)
        if (locationNameCompare !== 0) return locationNameCompare
        return a.position_type.localeCompare(b.position_type)
      })
  },
}

// Rating metrics
const mockGetPulseRatingMetrics: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/performance/rating_metrics',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const timePeriod =
      (request.data as PulseFilters).timePeriod ?? TimePeriod.Monthly
    const groupedData = groupBy(filteredData, (data) =>
      normalizeDateByTimePeriod(data.date, timePeriod)
    )

    return {
      metrics: Object.entries(groupedData)
        .map(([date, data]) => {
          return {
            shift_date: date,
            avg_worker_rating_by_manager: data.length
              ? data.reduce(
                  (acc, data) => acc + data.avgWorkerToManagerRating,
                  0
                ) / data.length
              : 0,
            count_worker_rating_by_manager: data.length,
            number_managers_rated_worker_below_3:
              data.reduce(
                (acc, data) => acc + data.numWorkersRatedManagerBelow3,
                0
              ) / data.length,
            avg_shift_rating_by_worker: data.length
              ? data.reduce(
                  (acc, data) => acc + data.avgShiftToWorkerRating,
                  0
                ) / data.length
              : 0,
            count_shift_rating_by_worker: data.length,
            number_workers_rated_shift_below_3:
              data.reduce(
                (acc, data) => acc + data.numShiftToWorkerRatedBelow3,
                0
              ) / data.length,
          } as RatingMetrics[0]
        })
        .sort((a, b) => b.shift_date.localeCompare(a.shift_date)),
    }
  },
}

// Top compliments
const mockGetPulseTopCompliments: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/performance/top_compliments',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const groupedByCompliment = groupBy(
      filteredData.filter((data) => !!data.compliment),
      (data) => data.compliment
    )

    return {
      top_compliments: Object.entries(groupedByCompliment)
        .map(
          ([compliment, data]) =>
            ({
              compliment: compliment,
              num_compliments: data.length,
            } as TopCompliments[0])
        )
        .sort((a, b) => b.num_compliments - a.num_compliments),
    } as { top_compliments: TopCompliments }
  },
}

// Top complaints
const mockGetPulseTopComplaints: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/performance/top_complaints',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const groupedByComplaint = groupBy(
      filteredData.filter((data) => !!data.complaint),
      (data) => data.complaint
    )

    return {
      top_complaints: Object.entries(groupedByComplaint)
        .map(
          ([complaint, data]) =>
            ({
              complaint: complaint,
              num_complaints: data.length,
            } as TopComplaints[0])
        )
        .sort((a, b) => b.num_complaints - a.num_complaints),
    }
  },
}

// Ratings timeseries
const mockGetPulseRatingsTimeseries: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/performance/ratings_timeseries',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const timePeriod =
      (request.data as PulseFilters).timePeriod ?? TimePeriod.Monthly

    const groupedData = groupBy(filteredData, (data) =>
      normalizeDateByTimePeriod(data.date, timePeriod)
    )

    return {
      ratings_timeseries: Object.entries(groupedData).map(([date, data]) => {
        return {
          shift_date: date,
          avg_worker_rating_by_shift: data.length
            ? data.reduce(
                (acc, data) => acc + data.avgWorkerToManagerRating,
                0
              ) / data.length
            : 0,
          avg_shift_rating_by_worker: data.length
            ? data.reduce((acc, data) => acc + data.avgShiftToWorkerRating, 0) /
              data.length
            : 0,
        } as RatingsTimeseries[0]
      }),
    }
  },
}

// Ratings by location
const mockGetPulseRatingsByLocation: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/pulse/performance/ratings_by_location',
      'POST'
    ),
  getResponse: async (request) => {
    const demoData = getDemoData()

    const filteredData = getFilteredPulseFillData(demoData, request)

    const groupedData = groupBy(
      filteredData,
      (data) => data.locationName + '||' + data.positionType
    )

    return {
      ratings_by_location: Object.entries(groupedData).map(([key, data]) => {
        const [locationName, positionType] = key.split('||')

        return {
          company_name: locationName,
          position_type: positionType,
          avg_worker_rating_by_shift: data.length
            ? data.reduce(
                (acc, data) => acc + data.avgWorkerToManagerRating,
                0
              ) / data.length
            : 0,
          avg_shift_rating_by_worker: data.length
            ? data.reduce((acc, data) => acc + data.avgShiftToWorkerRating, 0) /
              data.length
            : 0,
          count_worker_rating_by_shift: data.length,
          count_shift_rating_by_worker: data.length,
          number_workers_rated_shift_below_3: data.reduce(
            (acc, data) => acc + (data.avgShiftToWorkerRating < 4 ? 1 : 0),
            0
          ),
          number_shifts_rated_worker_below_3: data.reduce(
            (acc, data) => acc + (data.avgWorkerToManagerRating < 4 ? 1 : 0),
            0
          ),
        } as RatingsByLocation[0]
      }),
    }
  },
}

// Top workers
const mockGetPulseTopWorkers: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(request, '/company/pulse/performance/top_workers', 'POST'),
  getResponse: async () => {
    const demoData = getDemoData()

    return {
      top_workers: demoData.insights?.workerBench.topWorkers.map((worker) => {
        const positionIndex =
          demoData.positions.length > 0
            ? worker.worker.id % demoData.positions.length
            : 0
        return {
          worker_id: worker.worker.id,
          worker_most_recent_position_name:
            demoData.positions[positionIndex].name ?? '',
          worker_rating: worker.worker.rating,
          shift_count: worker.totalShifts,
          worker_name: worker.worker.name,
          worker_profile_pic_url: worker.worker.profilePicUrl,
        }
      }),
    } as { top_workers: TopWorkers }
  },
}

export const pulseMocks = [
  mockGetPulseCompanies,
  mockGetPulsePositionTypes,
  mockGetPulseFillMetrics,
  mockGetPulseFillRateTimeseries,
  mockGetPulseFillRateByCompany,
  mockGetPulseShiftNoticeMetrics,
  mockGetPulseShiftNoticeTimeseries,
  mockGetPulseShiftRateByNoticeTimeseries,
  mockGetPulseFillPayNoticeByLocation,
  mockGetPulsePayRateByPosition,
  mockGetPulseSpendMetrics,
  mockGetPulseSpendTimeseries,
  mockGetPulseSpendByLocation,
  mockGetPulseSpendPerWorkerTimeseries,
  mockGetPulsePayRateMetrics,
  mockGetPulsePayRateTimeseries,
  mockGetPulsePayRateByLocation,
  mockGetPulsePayRateMarketByLocation,
  mockGetPulseRatingMetrics,
  mockGetPulseTopCompliments,
  mockGetPulseTopComplaints,
  mockGetPulseRatingsTimeseries,
  mockGetPulseRatingsByLocation,
  mockGetPulseTopWorkers,
]
